<template>
    <div class="page-template-container">
        <div class="header">
            <page-header />
        </div>
        <div
            v-if="pageName !== 'NotFound'"
            id="main"
            ref="main"
            tabindex="-1"
            role="main"
            :class="['pagetemplate',
                     displaySmallOrExtraSmall ? 'pagetemplate-small' : '',
                     !displayLargeOrExtraLarge ? 'pagetemplate-m-s-xs' : '']"
        >
            <!-- Vasen palkki, näytetään mobiilissa nappien takaa full width -->
            <div
                v-show="!displaySmallOrExtraSmall || mobileLeftViewOpen"
                :class="[!displaySmallOrExtraSmall ? 'left-container' : 'left-container-small']"
            >
                <div
                    :class="[ 'slot-left',
                              displayLargeOrExtraLarge ? 'slot-left-xl-large' : '',
                              displayType === displayMedium ? 'slot-left-medium' : '',
                              displaySmallOrExtraSmall ? 'slot-left-small' : '' ]"
                >
                    <div
                        v-if="mobileLeftViewOpen"
                        class="slot-left-mobile-header"
                    >
                        <button
                            id=""
                            ref="closeLeftSlotButton1"
                            class="close-button"
                            :aria-label="crossIconAlt"
                            @click="closeLeftSlot"
                            @keydown.tab="trapFocus"
                        >
                            <img
                                :src="crossIcon"
                                class="close-left-slot-icon"
                                alt=""
                            >
                        </button>
                    </div>

                    <h1
                        ref="header"
                        tabindex="-1"
                        class="left-slot-header"
                    >
                        {{ leftSlotHeader }}
                    </h1>
                    <!-- eslint-disable vue/no-v-html -->
                    <p
                        class="left-slot-infotext"
                        v-html="leftSlotAdditionalInfoText"
                    />
                    <!--eslint-enable-->
                    <p id="content-info-text">
                        {{ $t("App.LeftSlot.InfoText.ChangesInContent") }}
                    </p>
                    <!--<content-change-info-text v-show="pageName !== 'Etusivu'"/>-->
                    <!-- Näytetään, jos isompi kuin small, tai jos mobiilissa avattu left-slot näkymä -->
                    <guide
                        v-show="hasFieldGuide"
                        id="field-guide-area"
                        :guide-text="fieldGuideText"
                        :is-mobile="displaySmallOrExtraSmall"
                    />
                    <left-slot
                        v-show="!displaySmallOrExtraSmall || showMobileLeftSlot"
                        :page-name="pageName"
                        :type-name="typeName"
                        :sub-topic="subTopicProp"
                        :whitelist-subjects="whitelistSubjects"
                        @page-notfound="setNotFoundPage"
                    /> <!-- Routerin parametri määrittää mikä näkymä vasempaan reunaan -->
                    <guide
                        v-show="hasGuide"
                        :guide-text="guideText"
                        :is-mobile="displaySmallOrExtraSmall"
                    />
                    <div
                        v-if="mobileLeftViewOpen"
                        class="slot-left-mobile-footer"
                    >
                        <button
                            ref="closeLeftSlotButton2"
                            type="button"
                            class="button primary close-left-slot-button"
                            @click="closeLeftSlot"
                            @keydown.tab="trapFocus"
                        >
                            {{ $t("App.PageTemplate.ReturnToMapButton") }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Oikea palkki, sisältää kartan ja oikean slotin. s, xs näkymässä oikea slotti tulee samaan sarakkeeseen kartan kanssa, eli kaikki tavara yhdessä sarakkeessa.-->
            <div
                :class="['right-container',
                         displayType === displayExtraLarge ? 'right-container-xl' : '',
                         displayType === displayLarge ? 'right-container-large' : '']"
            >
                <!-- Oikean slotin otsikko-komponentti, näytetään kartan päällä s, xs näkymässä -->
                <div
                    v-if="!displayLargeOrExtraLarge"
                    class="slot-right-header"
                >
                    <right-slot-header :page-name="pageName" />
                </div>

                <!-- Vasemman slotin tiedot nappien takaa, jos s, xs -->
                <!-- Etusivulla poikkeuksellisesti vasemman slotin tiedot näytetään (mikäli koko on 's' tai 'xs') kartan yläpuolella -->
                <div
                    v-if="displaySmallOrExtraSmall && pageName === 'Etusivu'"
                    class="frontpage-info"
                >
                    <mobilefrontpageinfo />
                </div>
                <div
                    v-else-if="displaySmallOrExtraSmall"
                    class="mobile-buttons"
                >
                    <button
                        type="button"
                        class="button primary change-subject-button"
                        @click="showLeftSlot"
                    >
                        {{ $t("App.PageTemplate.ChangeSubjectButton") }}
                    </button>
                    <button
                        v-show="hasGuide"
                        type="button"
                        class="button primary open-guide-button"
                        @click="showLeftSlotGuide"
                    >
                        {{ $t("App.PageTemplate.OpenGuideButton") }}
                    </button>
                </div>

                <!-- Keskislotti, sisältää kartan, ja kartan legendin -->
                <div
                    :class="[ 'slot-middle',
                              displayLargeOrExtraLarge ? 'slot-middle-xl-large' : '',
                              displayType === displayMedium ? 'slot-middle-medium' : '',
                              displayType === displaySmall ? 'slot-middle-small' : '',
                              displayType === displayExtraSmall ? 'slot-middle-extra-small' : '']"
                >
                    <a
                        class="skip-map"
                        href="#rightSlot"
                        @click="handleSkipLinkPress"
                    >{{ $t("App.SkipLink.SkipMapContent") }}</a>
                    <div
                        :class="['map-container',
                                 displaySmallOrExtraSmall ? 'map-container-small' : '']"
                    >
                        <lmap ref="map" />
                    </div>

                    <!-- Kartan legend kartan päällä, jos mobiili, kartan alapuolella full width -->
                    <div
                        :class="[!displaySmallOrExtraSmall ? 'map-legend-container' : '',
                                 displaySmallOrExtraSmall ? 'map-legend-container-s-xs' : '']"
                    >
                        <map-legend
                            :data="legend"
                            :title-name="mapLegendTitle"
                        />
                    </div>
                </div>

                <!-- Oikea slotti, sisältää otsikkokomponentin ja sisältökomponentin-->
                <div
                    id="rightSlot"
                    :class="['slot-right',
                             displayLargeOrExtraLarge ? 'slot-right-xl-large' : '',
                             displayType === displayMedium ? 'slot-right-medium' : '',
                             displaySmallOrExtraSmall ? 'slot-right-small' : '' ]"
                >
                    <right-slot-header v-if="displayLargeOrExtraLarge" />
                    <right-slot :guide-text="rightSlotGuideText" /> <!-- Storen kautta näytetään tarkemmat tiedot -->
                </div>

                <!-- Footer -->
                <div
                    :class="['footer',
                             displayType === displayExtraLarge ? 'footer-xl' : '',
                             displayType === displayLarge ? 'footer-large' : '' ,
                             !displayLargeOrExtraLarge && displayType !== displayExtraSmall ? 'footer-m-s' : '',
                             twoColumnsMobileFooter ? 'two-columns-mobile' : '',
                             displayType === displayExtraSmall ? 'footer-xs' : '']"
                >
                    <page-footer />
                </div>
            </div>
        </div>
        <div
            v-else
            :class="['pagetemplate',
                     displaySmallOrExtraSmall ? 'pagetemplate-small' : '',
                     !displayLargeOrExtraLarge ? 'pagetemplate-m-s-xs' : '']"
        >
            <div
                v-show="!displaySmallOrExtraSmall || mobileLeftViewOpen"
                :class="[!displaySmallOrExtraSmall ? 'left-container' : 'left-container-small']"
            >
                <div
                    :class="[ 'slot-left',
                              displayLargeOrExtraLarge ? 'slot-left-xl-large' : '',
                              displayType === displayMedium ? 'slot-left-medium' : '',
                              displaySmallOrExtraSmall ? 'slot-left-small' : '' ]"
                />
            </div>

            <div
                :class="['right-container',
                         displayType === displayExtraLarge ? 'right-container-xl' : '',
                         displayType === displayLarge ? 'right-container-large' : '']"
            >
                <div
                    :class="[ 'slot-middle',
                              displayLargeOrExtraLarge ? 'slot-middle-xl-large' : '',
                              displayType === displayMedium ? 'slot-middle-medium' : '',
                              displaySmallOrExtraSmall ? 'slot-middle-small' : '']"
                >
                    <not-found />
                </div>

                <div
                    :class="['slot-right',
                             displayLargeOrExtraLarge ? 'slot-right-xl-large' : '',
                             displayType === displayMedium ? 'slot-right-medium' : '',
                             displaySmallOrExtraSmall ? 'slot-right-small' : '' ]"
                />

                <!-- Footer -->
                <div
                    :class="['footer',
                             displayType === displayExtraLarge ? 'footer-xl' : '',
                             displayType === displayLarge ? 'footer-large' : '' ,
                             !displayLargeOrExtraLarge && displayType !== displayExtraSmall ? 'footer-m-s' : '',
                             twoColumnsMobileFooter ? 'two-columns-mobile' : '',
                             displayType === displayExtraSmall ? 'footer-xs' : '']"
                >
                    <page-footer />
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { Component, Prop, Vue, Watch } from "vue-facing-decorator"
    import LeftSlotTemplate from "@/templates/LeftSlotTemplate.vue"
    import RightSlotTemplate from "@/templates/RightSlotTemplate.vue"
    import RightSlotHeaderTemplate from "@/templates/RightSlotHeaderTemplate.vue"
    import MapClass from "@/map/Map.vue"
    import Footer from "@/layout/Footer.vue"
    import Header from "@/layout/Header.vue"
    import Guide from "@/components/Guide.vue"
    import MapLegend from "@/map/MapLegend.vue"
    import NotFound from "@/views/NotFound.vue"
    import SearchResultModel from "@/models/searchresultmodel"
    import MobileFrontPageInfo from "@/components/MobileFrontPageInfo.vue"
    import PageWhitelistUtils from "@/helpers/pageWhitelistUtils"
    import LocaleHelper from "@/helpers/localeHelper"
    import useEventsBus from '../eventBus'

    const { bus } = useEventsBus()

    @Component({
        components: {
            "left-slot": LeftSlotTemplate,
            "right-slot": RightSlotTemplate,
            "right-slot-header": RightSlotHeaderTemplate,
            lmap: MapClass,
            "page-footer": Footer,
            "page-header": Header,
            Guide,
            "map-legend": MapLegend,
            "not-found": NotFound,
            mobilefrontpageinfo: MobileFrontPageInfo
        }
    })
    export default class PageTemplate extends Vue {
        @Prop({ type: Boolean, default: false }) isNotFound!: boolean
        @Prop({ type: Boolean, default: true }) whitelistSubjects!: boolean // Suodatetaanko aihevalinnat whitelistan mukaan. Testeissä pitää voida ottaa pois päältä.

        mounted(): void {
            // Observer, to observe map width change
            const resizeObserver = new ResizeObserver(() => {
                this.reloadMap()
            })

            if (document.getElementById("map") !== null) {
                resizeObserver.observe(document.getElementById("map") as Element)
            }
        }

        pageName: string = ""
        typeName: string = ""
        topicName: string | null = null
        routeParamsPath: string = ""
        showMobileLeftSlot: boolean = false
        showMobileLeftSlotGuide: boolean = false
        showMobileGuide: boolean = false
        rightSlotChanged: boolean = false
        notFoundUrl: string = "NotFound"
        guideText: string = ""
        fieldGuideText: string = ""
        rightSlotGuideText: string = ""
        skipLinkClicked: boolean = false

        async created() {
            this.setPageNameWithRouteParameters()
        }

        // Vasemman slotin otsikko
        get leftSlotHeader(): string | undefined {
            switch (this.pageName) {
                case "Etusivu":
                    return this.$t("App.Header.Etusivu")
                case "Infokartat":
                    return this.$t("App.LeftSlot.Header.Infokartat")
                case "Tieliikenne":
                    return this.$t("App.LeftSlot.Header.TieLiikenne")
                case "Vesiliikenne":
                    return this.$t("App.LeftSlot.Header.VesiLiikenne")
                case "LaajakaistaJaPuhelin":
                    return this.$t("App.LeftSlot.Header.LaajakaistaJaPuhelin")
                case "Posti":
                    return this.$t("App.LeftSlot.Header.Posti")
                case "TVJaRadio":
                    return this.$t("App.LeftSlot.Header.TVJaRadio")
                case "ViatJaHairiot":
                    return this.$t("App.LeftSlot.Header.ViatJaHairiot")
                case "Radiomikrofonitaajuudet":
                    return this.$t("App.Header.Viestinta.Radiomikrofonitaajuudet")
                case "Bittimittari":
                    return this.$t("App.LeftSlot.Header.Bittimittari")
                default:
                    return ""
            }
        }

        // Vasemman slotin lisäinfoteksti
        get leftSlotAdditionalInfoText(): string | undefined {
            switch (this.pageName) {
                case "Bittimittari":
                    return this.$t("App.Bittimittari.LeftSlot.InfoText")
                default:
                    return ""
            }
        }

        // Oikean slotin komponentin nimi
        get rightSlot(): string {
            this.rightSlotChanged = true
            return this.$store.getters.rightSlot
        }

        get rightSlotName(): string {
            return this.$store.getters.rightSlot
        }

        get mapLegendTitle(): string {
            if (this.rightSlotName === "Infokartat") {
                return ""
            }
            return this.rightSlotName
        }

        get legend(): string {
            return this.$store.getters.legend
        }

        // ruutukoko
        get displayType(): string {
            return this.$store.getters.displayType
        }

        get twoColumnsMobileFooter(): boolean {
            return this.$store.getters.twoColumnsMobileFooter
        }

        get displayLarge(): string {
            return this.$store.state.displayLarge
        }

        get displayExtraLarge(): string {
            return this.$store.state.displayExtraLarge
        }

        get displayMedium(): string {
            return this.$store.state.displayMedium
        }

        get displaySmall(): string {
            return this.$store.state.displaySmall
        }

        get displayExtraSmall(): string {
            return this.$store.state.displayExtraSmall
        }

        // ruutukoko on isompi kuin medium
        get displayLargeOrExtraLarge(): boolean {
            return this.displayType === this.displayExtraLarge || this.displayType === this.displayLarge
        }

        get displaySmallOrExtraSmall(): boolean {
            return this.displayType === this.displaySmall || this.displayType === this.displayExtraSmall
        }

        get crossIcon(): string {
            return this.$store.getters.contentServiceUrl + "/Content/icons/svg-blue/ruksi.svg" + "?" + this.$store.getters.contentServiceTimeStamp
        }

        get crossIconAlt(): string | undefined {
            return this.$t("App.Header.CrossIconAlt")
        }

        get mobileLeftViewOpen(): boolean {
            return this.showMobileLeftSlot || this.showMobileLeftSlotGuide
        }

        get hasGuide(): boolean {
            // Guidea ei näytetä jos sille ei ole tekstiä
            return this.guideText !== ""
        }

        get hasFieldGuide(): boolean {
            return this.fieldGuideText !== ""
        }

        get selectedSubjectRadioButton(): SearchResultModel {
            return this.$store.getters.selectedSubjectRadioButton
        }

        @Watch("selectedSubjectRadioButton")
        async onSelectedSubjectRadioButton(): Promise<void> {
            this.updateGuideText()
            this.updateFieldGuideText()
            this.updateRightSlotGuideText()
            this.isUrlParamValid()
        }

        get addressInputFocus() {
            return bus.value.get('addressInputFocus')
        }

        @Watch('addressInputFocus')
        async onAddressInputFocus(): Promise<void> {
            if (this.displaySmallOrExtraSmall) {
                this.showMobileLeftSlot = true
                this.scrollToTop()
            }
        }

        get subTopicProp(): string | null {
            this.topicName = null
            if (this.$store.getters.subTopic) {
                this.topicName = this.$store.getters.subTopic
            }
            else if (this.$route.query.subTopic) {
                this.topicName = this.$route.query.subTopic as string
                this.$store.dispatch("setSubTopic", this.topicName)
            }
            return this.topicName
        }

        @Watch("subTopicString")
        onSubTopicChange() {
            if (this.subTopicString && this.$route.query.subTopic) {
                this.topicName = this.subTopicString
            }
        }

        get subTopicString(): string | null {
            return this.$store.getters.subTopic
        }

        @Watch("$route.params")
        onRouterParamChange() {
            // jos sivu vaihtuu eikä ole subTopic-url-parametria, nollataan se myös Storesta.
            if (!this.$route.query.subTopic && this.$store.getters.subTopic) {
                this.$store.dispatch("setSubTopic", null)
            }

            // skip linkkiä käytettäessä ei nollata rightSlot eikä rightSlotDatan tietoja
            if (!this.skipLinkClicked && !this.$store.getters.skipToContentClicked) {
                // Nollataan right slot sivusiirtymissä
                this.$store.dispatch("setRightSlot", "")
                // Nollataan myös rightslotdata
                this.$store.dispatch("setRightSlotData", [])
                this.setPageNameWithRouteParameters()
                this.$store.dispatch("setSelectedSubjectRadioButton", undefined)
            }
            this.$store.dispatch("setSkipToContentClicked", false)
            this.skipLinkClicked = false
        }

        @Watch("displaySmallOrExtraSmall")
        onDisplaySmallOrExtraSmallChange(): void {
            // Jos ruutukokoa muutetaan pienestä suureksi, piilotetaan mobiilissa näytettävät kentät
            if (!this.displaySmallOrExtraSmall) {
                this.showMobileLeftSlot = false
                this.showMobileLeftSlotGuide = false
            }
        }

        get activeLang(): SearchResultModel {
            return this.$store.getters.activeLang
        }

        @Watch("activeLang")
        async onActiveLang(): Promise<void> {
            this.updateGuideText()
            this.updateFieldGuideText()
            this.updateRightSlotGuideText()
        }

        reloadMap(): void {
            // if right slot visibility have been changed, update map
            if (this.rightSlotChanged) {
                (this.$refs.map as any).$refs.leafletMap.invalidateSize()
                this.rightSlotChanged = false
            }
        }

        setPageNameWithRouteParameters(): void {
            if (this.isNotFound && this.$route.params.catchAll !== "") {
                this.setNotFoundPage()
                return
            }
            // Jos pagename ei löydy, asetetaan tyyppi /Etusivu || /Liikenne/Ilmailu
            this.typeName = ""
            if (this.$route.params.type) {
                this.typeName = this.$route.params.type as string
            }
            if (!this.$route.params.pageName) {
                if (!this.$route.params.type) {
                    this.pageName = "Etusivu"
                }
                else {
                    this.pageName = this.$route.params.type as string
                }
            }
            else {
                this.pageName = this.$route.params.pageName as string
            }

            // Tarkistetaan löytyykö sivu whitelistalta
            const pageWhitelistUtils = new PageWhitelistUtils()
            if (this.pageName !== "Etusivu" && this.whitelistSubjects && !pageWhitelistUtils.isMenuOnWhiteList(this.pageName)) {
                this.setNotFoundPage()
            }
            else {
                this.updateGuideText()
                this.updateFieldGuideText()
                this.updateRightSlotGuideText()
            }
        }

        updateGuideText(): void {
            // Päivitetään Guide-komponentin teksti
            let key = ""
            if ((this.$route.params.pageName === "TVJaRadio"
                || this.$route.params.pageName === "LaajakaistaJaPuhelin"
                || this.$route.params.pageName === "Vesiliikenne"
                || this.$route.params.pageName === "Tieliikenne"
                || this.$route.params.pageName === "Posti")
                && this.$store.getters.selectedSubjectRadioButton
                && this.$store.getters.selectedSubjectRadioButton.radioValue) {
                // Tekstiresurssi haetaan radiobuttonin perusteella
                key = this.$store.getters.selectedSubjectRadioButton.radioValue
            }
            else {
                // Tekstiresurssi haetaan sivun nimen perusteella
                key = this.pageName
            }
            key = "App." + key + ".GuideText"

            let guideText = ""
            if (this.$route.params.pageName === "Radiomikrofonitaajuudet") {
                // muokataan pilkut valitun kielen mukaan
                const minFrequency = LocaleHelper.formatDecimal(this.$store.getters.minMaxRadioMFrequencies.min.toFixed(2))
                const maxFrequency = LocaleHelper.formatDecimal(this.$store.getters.minMaxRadioMFrequencies.max.toFixed(2))

                const limits = minFrequency + " - " + maxFrequency
                guideText = this.$t(key, [limits])
            }
            else {
                guideText = this.$t(key)
            }

            if (guideText === key) {
                // Tekstiresurssia ei löytynyt
                this.guideText = ""
            }
            else {
                this.guideText = guideText !== undefined ? guideText : ""
            }
        }

        updateFieldGuideText(): void {
            let key = ""
            if ((this.$route.params.pageName === "TVJaRadio"
                || this.$route.params.pageName === "LaajakaistaJaPuhelin"
                || this.$route.params.pageName === "Vesiliikenne"
                || this.$route.params.pageName === "Tieliikenne"
                || this.$route.params.pageName === "Posti")
                && this.$store.getters.selectedSubjectRadioButton
                && this.$store.getters.selectedSubjectRadioButton.radioValue) {
                key = this.$store.getters.selectedSubjectRadioButton.radioValue
            }
            else {
                key = this.pageName
            }

            switch (key) {
                case "Radiomikrofonitaajuudet":
                    this.fieldGuideText = this.guideText
                    this.guideText = ""
                    break
                case "Bittimittari":
                    this.fieldGuideText = ""
                    break
                default:
                    this.fieldGuideText = ""
                    break
            }
        }

        isUrlParamValid(): void {
            const pageWhitelistUtils = new PageWhitelistUtils()
            if (this.subTopicProp && (this.pageName === "Infokartat" || this.pageName === "LaajakaistaJaPuhelin")) {
                if (this.pageName === "Infokartat") {
                    if (!pageWhitelistUtils.isUrlParamInfokartta(this.subTopicProp)) {
                        this.setNotFoundPage()
                    }
                }
                else if (this.pageName === "LaajakaistaJaPuhelin") {
                    if (!pageWhitelistUtils.isUrlParamInfokartta(this.subTopicProp)
                        && !pageWhitelistUtils.isSubjectOnWhiteList(this.subTopicProp)
                        && this.subTopicProp !== "OikeusPuhelinJaLaajakaistaLiittymaanSijainninMukaan"
                        && this.subTopicProp !== "OikeusPuhelinJaLaajakaistaLiittymaanPalvelutyypinMukaan") {
                        this.setNotFoundPage()
                    }
                }
            }

            if (this.subTopicProp && (this.pageName === "Infokartat" || this.pageName === "Posti")) {
                if (this.pageName === "Infokartat") {
                    if (!pageWhitelistUtils.isUrlParamInfokartta(this.subTopicProp)) {
                        this.setNotFoundPage()
                    }
                }
                else if (this.pageName === "Posti") {
                    if (!pageWhitelistUtils.isUrlParamInfokartta(this.subTopicProp)
                        && this.subTopicProp !== "PostiHaeToimipaikkoja"
                        && this.subTopicProp !== "PostiTarkasteleMaaraa") {
                        this.setNotFoundPage()
                    }
                }
            }
        }

        updateRightSlotGuideText(): void {
            // Päivitetään RightSlotin Guide teksti
            let key = ""
            if (this.rightSlotName === "Infokartat" || (this.rightSlotName === "" && this.pageName === "Infokartat")) {
                // Kaikille infokartoille näytetään sama teksti Guidessa
                key = "App.Infokartat.RightSlot.GuideText"
            }
            else if (this.$store.getters.selectedSubjectRadioButton) {
                // Tekstiresurssi haetaan radiobuttonin perusteella
                key = "App." + this.$store.getters.selectedSubjectRadioButton.radioValue + ".RightSlot.GuideText"
            }

            const guideText = this.$t(key)
            if (guideText === key) {
                // Tekstiresurssia ei löytynyt
                this.rightSlotGuideText = ""
            }
            else {
                this.rightSlotGuideText = guideText !== undefined ? guideText : ""
            }
        }

        trapFocus(event: KeyboardEvent) {
            const firstTabStop = this.$refs.closeLeftSlotButton1 as HTMLButtonElement
            const lastTabStop = this.$refs.closeLeftSlotButton2 as HTMLButtonElement

            // Tarkistetaan, mikä event ja mikä modaalin elementti kyseessä ja siirrytään sen mukaan
            if (event.key === "Tab" || event.keyCode === 9) {
                if (event.shiftKey) /* shift + tab */ {
                    if (document.activeElement === firstTabStop) {
                        event.preventDefault()
                        lastTabStop?.focus()
                    }
                }
                else /* tab */ {
                    if (document.activeElement === lastTabStop) {
                        event.preventDefault()
                        firstTabStop?.focus()
                    }
                }
            }
        }

        showLeftSlot(): void {
            this.showMobileLeftSlot = true
            this.scrollToTop()
            this.$nextTick(() => {
                (this.$refs.closeLeftSlotButton1 as HTMLButtonElement).focus()
            })
        }

        showLeftSlotGuide(): void {
            this.showMobileLeftSlotGuide = true
            this.scrollToTop()
            this.$nextTick(() => {
                (this.$refs.closeLeftSlotButton1 as HTMLButtonElement).focus()
            })
        }

        closeLeftSlot(): void {
            if (this.showMobileLeftSlot) {
                this.showMobileLeftSlot = false
            }
            else if (this.showMobileLeftSlotGuide) {
                this.showMobileLeftSlotGuide = false
            }
        }

        setNotFoundPage(): void {
            this.pageName = "NotFound"
        }

        scrollToTop() {
            window.scrollTo(0, 0)
        }

        handleSkipLinkPress() {
            this.skipLinkClicked = true
        }
    }
</script>

<style scoped>

    /* Huom: Jos muokkaat kokonaisrakennetta, muista huomioida, että myös modal ja header tyylit pysyvät kunnossa */
    .page-template-container {
        max-width: 1920px;
        width: 100%;
        height: 100%;
        margin-left: auto;
        margin-right: auto;
        background-color: white;
        /* Scoped variables */
        --var-footer-height: 12rem;
        --var-footer-m-s-height: 18.0625rem;
        --var-footer-xs-height: 24.3125rem;
        --var-header-height: 5rem; /* Height + border bottom*/
    }

    .pagetemplate {
        display: flex;
        height: calc(100% - var(--var-header-height));
    }

    .pagetemplate-m-s-xs {
        height: auto;
        position: relative;
        overflow-x: hidden;
    }

    .pagetemplate-small {
        flex-wrap: wrap;
        flex-direction: column;
    }

    .page-template-container > .header {
        position: relative;
        z-index: 1045;
    }

    .left-container-small {
        z-index: 40;
    }

    .right-container {
        flex-grow: 1;
        display: flex;
        flex-wrap: wrap;
        flex-direction: column;
        background-color: white;
        position: relative;
        z-index: 20;
    }

    .right-container-xl {
        flex-direction: row;
    }

    .right-container-large {
        flex-direction: row;
    }

    .mobile-buttons {
        width: 100%;
        justify-content: flex-start;
        display: flex;
        padding: 15px;
    }

        .mobile-buttons .change-subject-button {
            margin-right: 5px;
        }

    .change-subject-button:focus,
    .close-left-slot-button:focus,
    .close-button:focus {
        outline: 3px solid var(--Traficom-main-color);
        outline-offset: 2px;
    }

    .slot-left-xl-large {
        flex-grow: 1;
        width: var(--var-slot-width-xl-l);
        overflow-y: auto;
        height: calc(100vh - var(--var-header-height));
    }

    .slot-left-medium {
        flex-grow: 1;
        width: var(--var-slot-width-xl-l); /* Määritelty App.vue tasolla */
    }

    .slot-left-small {
        height: 100%;
        width: 100%;
        position: absolute;
        z-index: 10040;
        background-color: white;
    }

    .slot-left-mobile-header {
        text-align: right;
        margin: 20px;
    }

    .close-button {
        background: none;
        border: none;
    }

    .close-left-slot-icon {
        border: none;
        width: 18px;
        cursor: pointer;
    }

    .slot-left-mobile-footer {
        text-align: left;
        margin: 20px;
    }

    /* Styles for each display type */
    .slot-middle {
        flex-grow: 1;
        position: relative;
    }

    /* if right slot not shown */
    .slot-middle-full-width {
        width: 100%;
    }

    .slot-middle-xl-large {
        height: calc(100% - var(--var-footer-height));
        order: 1;
    }

    .slot-middle-medium {
        height: calc(100vh - var(--var-footer-height) - var(--var-header-height));
        margin-bottom: 20px;
    }

    .slot-middle-small {
       /* Täytyy olla jokin korkeus määriteltynä, jotta minimikorkeus toimii*/
        height: calc(100vh - var(--var-footer-height) - var(--var-header-height));
        min-height: 400px;
        display: flex;
        flex-direction: column;
        margin-bottom: 20px;
    }

    .slot-middle-extra-small {
        height: 100vh;
        display: flex;
        flex-direction: column;
        margin-bottom: 20px;
    }

    .slot-right-xl-large {
        width: var(--var-slot-width-xl-l);
        order: 2;
        word-wrap: break-word;
        overflow-y: auto;
        height: calc(100vh - var(--var-footer-height) - var(--var-header-height));
    }

    .map-container {
        height: 100%;
    }

    .map-container-small {
        height: 100%;
    }

    .map-legend-container {
        position: absolute;
        right: 20px;
        bottom: 20px;
        z-index: 10020;
        box-shadow: 0px 2px 6px #00000029;
        word-break: break-word;
    }

    .map-legend-container-s-xs {
        max-height: 50%;
        width: 100%;
        align-self: end;
        box-shadow: 0px 2px 6px #00000029;
    }

    .map-legend-container :deep(.legend i) {
        margin-top: 0px !important;
    }

    /* includes tablet & mobile */
    .slot-right-medium {
        flex-grow: 1;
        word-wrap: break-word;
    }

    .slot-right-small {
        width: 100%;
        word-wrap: break-word;
    }

    .footer {
        background-color: var(--Traficom-main-color);
        color: white;
        width: 100%;
        height: var(--var-footer-height);
        z-index: 1045;
    }

    .footer-xl {
        order: 3;
    }

    .footer-large {
        order: 3;
    }

    .footer-m-s {
        height: var(--var-footer-m-s-height);
    }

        .footer-m-s.two-columns-mobile {
            height: var(--var-footer-height);
        }

    .footer-xs {
        height: var(--var-footer-xs-height);
    }

    .frontpage-info {
        padding: 0rem 1.25rem;
    }

    .left-slot-header,
    .left-slot-infotext {
        padding: 0rem 1.25rem;
    }

    a.skip-map {
        left: -999px;
        position: absolute;
        top: auto;
        width: 1px;
        height: 1px;
        overflow: hidden;
        z-index: -999;
    }

        a.skip-map:focus, a.skip-map:active {
            left: 50%;
            top: 60px;
            width: auto;
            height: auto;
            overflow: auto;
            margin: 10px;
            padding: 13px;
            text-align: center;
            font-size: 1.2em;
            z-index: 1002;
            background-color: var(--Traficom-main-color);
            color: white;
            font-weight: 400;
            background-image: none;
            background-size: unset;
            background-position: unset;
            background-repeat: unset;
            text-decoration: unset;
            cursor: pointer;
            outline: 3px solid var(--Traficom-main-color);
            outline-offset: 2px;
        }

        a.skip-map:hover {
            text-decoration: underline;
        }

    #content-info-text {
        padding: 0rem 1.25rem 1rem 1.25rem;
        margin: 0rem;
    }
</style>
<style>
    div.marker-cluster-small,
    div.marker-cluster-medium,
    div.marker-cluster-large {
        background-color: unset;
        opacity: 1 !important;
    }

    div.marker-cluster div {
        color: #FFFFFF;
        font-size: 1rem;
        width: 1.8em;
        height: 1.8em;
        display: flex;
        justify-content: center;
        align-items: center;
        text-align: center;
        overflow: hidden;
    }

    div.marker-cluster-small div,
    div.marker-cluster-medium div,
    div.marker-cluster-large div {
        background-color: #1C1C1C;
    }
</style>
